import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import { FormSectionPanel } from '../../../../components/common';
import { PermissionGuard } from '../../../../auth/components/PermissionGuard';
import { GuiaSalidaService } from '../services/GuiaSalidaService';
import { guiaSalidaPdfService } from '../services/guiaSalidaPdfService';
import { useGuiaSalidaStore } from '../store/useGuiaSalidaStore';
import {
  clienteService,
  type ClienteSearch,
} from '../../partes-produccion/services/clienteService';
import type {
  EstadoType,
  GuiaSalida,
  GuiaSalidaRequest,
  OrigenSalida,
  StockDisponibleItem,
  TipoSalida,
} from '../types';
import {
  estadoLabels,
  origenColors,
  origenLabels,
} from '../types';

interface DetalleEditState {
  idGuiaProduccionDetalle: number;
  origen: OrigenSalida;
  cantidad: number;
  // Heredados del catalogo:
  pesoEmpaqueInterior: number;
  cantidadDisponible: number;
}

const todayIso = (): string => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const stockKey = (item: { idGuiaProduccionDetalle: number; origen: OrigenSalida }) =>
  `${item.idGuiaProduccionDetalle}|${item.origen}`;

const GuiaSalidaFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const editingId = idParam && idParam !== 'nueva' ? Number(idParam) : null;

  const { createGuia, updateGuia, updateEstado } = useGuiaSalidaStore();

  // ----- Form state -----
  const [fechaSalida, setFechaSalida] = useState<string>(todayIso());
  const [tipoSalida, setTipoSalida] = useState<TipoSalida>('FRESCO');
  const [placaVehiculo, setPlacaVehiculo] = useState<string>('');
  const [observaciones, setObservaciones] = useState<string>('');
  const [selectedCliente, setSelectedCliente] = useState<ClienteSearch | null>(null);
  const [clienteOptions, setClienteOptions] = useState<ClienteSearch[]>([]);
  const [clienteLoading, setClienteLoading] = useState(false);
  const [idCliente, setIdCliente] = useState<number | null>(null);

  // Stock available items
  const [stock, setStock] = useState<StockDisponibleItem[]>([]);
  const [loadingStock, setLoadingStock] = useState(false);

  // Selected items map: key -> { cantidad }
  const [selected, setSelected] = useState<Record<string, DetalleEditState>>({});

  // Existing guia (when editing)
  const [guia, setGuia] = useState<GuiaSalida | null>(null);
  const [loadingGuia, setLoadingGuia] = useState(false);
  const [updatingEstado, setUpdatingEstado] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const isReadOnly = guia != null && guia.estado !== 'PEN';

  // ----- Initial load -----

  const loadStock = useCallback(async () => {
    setLoadingStock(true);
    try {
      const data = await GuiaSalidaService.getStockDisponible(editingId ?? undefined);
      setStock(data);
    } catch (e) {
      console.error(e);
      setError('Error al cargar el stock disponible.');
    } finally {
      setLoadingStock(false);
    }
  }, [editingId]);

  const loadGuia = useCallback(async () => {
    if (editingId == null) return;
    setLoadingGuia(true);
    try {
      const g = await GuiaSalidaService.getById(editingId);
      setGuia(g);
      setFechaSalida(g.fechaSalida ? g.fechaSalida.slice(0, 10) : todayIso());
      setTipoSalida(g.tipoSalida);
      setPlacaVehiculo(g.placaVehiculo ?? '');
      setObservaciones(g.observaciones ?? '');
      setIdCliente(g.idCliente ?? null);
      if (g.cliente && g.idCliente) {
        setSelectedCliente({
          id: g.idCliente,
          codigo: '',
          razonSocial: g.cliente.razonSocial ?? '',
        });
      }
    } catch (e) {
      console.error(e);
      setError('Error al cargar la Guia de Salida.');
    } finally {
      setLoadingGuia(false);
    }
  }, [editingId]);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  useEffect(() => {
    loadGuia();
  }, [loadGuia]);

  // Hidratar `selected` cuando llegan stock + guia (al editar)
  useEffect(() => {
    if (!guia || stock.length === 0) return;

    const next: Record<string, DetalleEditState> = {};
    for (const det of guia.detalles) {
      const k = stockKey(det);
      const stockItem = stock.find(
        (s) =>
          s.idGuiaProduccionDetalle === det.idGuiaProduccionDetalle && s.origen === det.origen
      );
      // El stock disponible YA excluye lo aprobado en otras guias y lo de la propia (excludeGuiaSalidaId).
      // Para una guia PEN, su propio consumo no descuenta (porque solo APR descuenta).
      next[k] = {
        idGuiaProduccionDetalle: det.idGuiaProduccionDetalle,
        origen: det.origen,
        cantidad: det.cantidad,
        pesoEmpaqueInterior: stockItem?.pesoEmpaqueInterior
          ?? Number(det.guiaProduccionDetalle?.articulo?.pesoEmpaqueInterior ?? 0),
        cantidadDisponible: stockItem?.cantidadDisponible ?? det.cantidad,
      };
    }
    setSelected(next);
  }, [guia, stock]);

  // ----- Cliente search -----

  const handleClienteInputChange = async (
    _event: React.SyntheticEvent,
    value: string
  ) => {
    if (!value || value.length < 2) {
      setClienteOptions([]);
      return;
    }
    setClienteLoading(true);
    try {
      const results = await clienteService.search(value);
      setClienteOptions(results);
    } catch (e) {
      console.error('Error searching clientes:', e);
      setClienteOptions([]);
    } finally {
      setClienteLoading(false);
    }
  };

  const handleClienteChange = (
    _event: React.SyntheticEvent,
    value: ClienteSearch | null
  ) => {
    setSelectedCliente(value);
    setIdCliente(value?.id ?? null);
  };

  // ----- Stock selection -----

  const toggleSelected = (item: StockDisponibleItem) => {
    if (isReadOnly) return;
    const k = stockKey(item);
    setSelected((prev) => {
      const next = { ...prev };
      if (next[k]) {
        delete next[k];
      } else {
        next[k] = {
          idGuiaProduccionDetalle: item.idGuiaProduccionDetalle,
          origen: item.origen,
          cantidad: item.cantidadDisponible,
          pesoEmpaqueInterior: item.pesoEmpaqueInterior,
          cantidadDisponible: item.cantidadDisponible,
        };
      }
      return next;
    });
  };

  const updateCantidad = (item: StockDisponibleItem, value: string) => {
    if (isReadOnly) return;
    const k = stockKey(item);
    const num = Number.parseFloat(value);
    setSelected((prev) => {
      const cur = prev[k];
      if (!cur) return prev;
      return {
        ...prev,
        [k]: {
          ...cur,
          cantidad: Number.isFinite(num) ? num : 0,
        },
      };
    });
  };

  // ----- Calculations -----

  const summary = useMemo(() => {
    let totalKilos = 0;
    let totalCantidad = 0;
    let totalItems = 0;
    let invalido = false;

    for (const k of Object.keys(selected)) {
      const sel = selected[k];
      totalItems += 1;
      totalCantidad += sel.cantidad;
      totalKilos += sel.cantidad * sel.pesoEmpaqueInterior;
      if (sel.cantidad <= 0 || sel.cantidad > sel.cantidadDisponible + 0.0001) {
        invalido = true;
      }
    }
    return { totalKilos, totalCantidad, totalItems, invalido };
  }, [selected]);

  // ----- Save -----

  const buildRequest = (): GuiaSalidaRequest => ({
    fechaSalida: new Date(fechaSalida).toISOString(),
    idCliente,
    tipoSalida,
    placaVehiculo: placaVehiculo || null,
    observaciones: observaciones || null,
    detalles: Object.values(selected).map((s) => ({
      idGuiaProduccionDetalle: s.idGuiaProduccionDetalle,
      origen: s.origen,
      cantidad: s.cantidad,
      kilos: s.cantidad * s.pesoEmpaqueInterior,
    })),
  });

  const validate = (): string | null => {
    if (Object.keys(selected).length === 0) return 'Debe seleccionar al menos un item del stock disponible.';
    if (summary.invalido) return 'Hay cantidades invalidas o que exceden el stock disponible.';
    return null;
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMsg(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    try {
      const req = buildRequest();
      let result: GuiaSalida | null;
      if (editingId != null) {
        result = await updateGuia(editingId, req);
      } else {
        result = await createGuia(req);
      }
      if (result) {
        setSuccessMsg(editingId != null ? 'Guia actualizada.' : `Guia ${result.numero} creada.`);
        setTimeout(() => navigate('/produccion/guias-salida'), 1200);
      } else {
        setError('No se pudo guardar la Guia de Salida. Revise los mensajes.');
      }
    } catch (e) {
      console.error(e);
      setError('Error al guardar la Guia de Salida.');
    } finally {
      setSaving(false);
    }
  };

  const handleEstadoChange = async (event: SelectChangeEvent) => {
    const nuevoEstado = event.target.value as EstadoType;
    if (!guia) return;
    setUpdatingEstado(true);
    setError(null);
    try {
      const ok = await updateEstado(guia.id, nuevoEstado);
      if (ok) {
        setGuia({ ...guia, estado: nuevoEstado });
        setSuccessMsg(`Estado actualizado a ${estadoLabels[nuevoEstado]}`);
        setTimeout(() => navigate('/produccion/guias-salida'), 1200);
      }
    } catch (e) {
      console.error(e);
      setError('Error al cambiar el estado.');
    } finally {
      setUpdatingEstado(false);
    }
  };

  const handlePrint = () => {
    if (!guia) return;
    try {
      guiaSalidaPdfService.generatePdf({
        numero: guia.numero,
        fechaSalida: guia.fechaSalida,
        cliente: selectedCliente?.razonSocial || guia.cliente?.razonSocial || null,
        tipoSalida: guia.tipoSalida,
        placaVehiculo: guia.placaVehiculo,
        observaciones: guia.observaciones,
        detalles: (guia.detalles || []).map((d) => {
          const stockItem = stock.find(
            (s) =>
              s.idGuiaProduccionDetalle === d.idGuiaProduccionDetalle && s.origen === d.origen
          );
          return {
            origen: d.origen,
            numeroDerc:
              stockItem?.numeroDerc
              ?? d.guiaProduccionDetalle?.guiaProduccion?.guiaRecepcion?.documento?.numeroDocumento
              ?? null,
            numeroGuiaProduccion:
              stockItem?.numeroGuiaProduccion
              ?? d.guiaProduccionDetalle?.idGuiaProduccion
              ?? d.guiaProduccionDetalle?.guiaProduccion?.id
              ?? 0,
            articulo: stockItem?.descripcionArticulo ?? d.guiaProduccionDetalle?.articulo?.descripcion ?? null,
            presentacion: stockItem?.presentacion ?? d.guiaProduccionDetalle?.articulo?.presentacion?.descripcion ?? null,
            calibre: stockItem?.calibre ?? d.guiaProduccionDetalle?.articulo?.calibre?.descripcion ?? null,
            cantidad: d.cantidad,
            kilos: d.kilos,
          };
        }),
      });
    } catch (e) {
      console.error('Error generating PDF', e);
      setError('Error al generar el PDF.');
    }
  };

  // ----- Render -----

  if (loadingGuia && !guia) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, width: '100%', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/produccion/guias-salida')}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Typography variant="h5" component="h1" fontWeight={600} sx={{ flex: 1 }}>
            {guia ? `Guia de Salida N° ${guia.numero}` : 'Nueva Guia de Salida'}
          </Typography>
          {guia && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ mr: 2 }}
            >
              Imprimir PDF
            </Button>
          )}
        </Box>

        {/* Estado */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label="GUIA DE SALIDA" color="info" size="medium" variant="filled" sx={{ fontWeight: 600 }} />
          {guia && (
            <PermissionGuard
              permission="SALIDA_APROBAR"
              fallback={<Chip label={estadoLabels[guia.estado]} size="small" />}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Estado:</Typography>
                <Select
                  value={guia.estado}
                  onChange={handleEstadoChange}
                  size="small"
                  sx={{ minWidth: 160 }}
                  disabled={updatingEstado || guia.estado === 'ANU'}
                >
                  <MenuItem value="PEN">
                    <Chip label="Pendiente" color="warning" size="small" />
                  </MenuItem>
                  <MenuItem value="APR" disabled={guia.estado === 'APR'}>
                    <Chip label="Aprobado" color="success" size="small" />
                  </MenuItem>
                  <MenuItem value="ANU">
                    <Chip label="Anulado" color="error" size="small" />
                  </MenuItem>
                </Select>
                {updatingEstado && <CircularProgress size={20} />}
              </Box>
            </PermissionGuard>
          )}
        </Box>

        {successMsg && (
          <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
            {successMsg}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Datos generales */}
        <FormSectionPanel title="Datos Generales">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              label="Fecha"
              type="date"
              size="small"
              value={fechaSalida}
              onChange={(e) => setFechaSalida(e.target.value)}
              disabled={isReadOnly}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <FormControl size="small" fullWidth disabled={isReadOnly}>
              <InputLabel>Tipo de Salida</InputLabel>
              <Select
                value={tipoSalida}
                label="Tipo de Salida"
                onChange={(e) => setTipoSalida(e.target.value as TipoSalida)}
              >
                <MenuItem value="FRESCO">Fresco</MenuItem>
                <MenuItem value="CONGELADO">Congelado</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Placa Vehiculo"
              size="small"
              value={placaVehiculo}
              onChange={(e) => setPlacaVehiculo(e.target.value.toUpperCase())}
              disabled={isReadOnly}
              fullWidth
              inputProps={{ maxLength: 20 }}
            />

            <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
              <Autocomplete
                options={clienteOptions}
                value={selectedCliente}
                onChange={handleClienteChange}
                onInputChange={handleClienteInputChange}
                getOptionLabel={(option) =>
                  `${option.razonSocial}${option.nombreFantasia ? ` (${option.nombreFantasia})` : ''}`
                }
                loading={clienteLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="Escriba para buscar clientes..."
                loadingText="Buscando..."
                disabled={isReadOnly}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente" placeholder="Buscar cliente..." size="small" />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.razonSocial}</Typography>
                      {option.nombreFantasia && (
                        <Typography variant="caption" color="text.secondary">
                          {option.nombreFantasia}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {option.codigo}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Box>

            <TextField
              label="Observaciones"
              size="small"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              disabled={isReadOnly}
              multiline
              rows={2}
              fullWidth
              sx={{ gridColumn: { xs: '1', md: '1 / span 3' } }}
              inputProps={{ maxLength: 800 }}
            />
          </Box>
        </FormSectionPanel>

        {/* Stock disponible */}
        <FormSectionPanel title="Stock Disponible (Producto B y Devolucion Interna)">
          {loadingStock ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : stock.length === 0 ? (
            <Alert severity="info">No hay stock disponible para liberar.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell padding="checkbox">Sel.</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell>DERC</TableCell>
                    <TableCell align="center">Guia</TableCell>
                    <TableCell>Articulo</TableCell>
                    <TableCell>Presentacion</TableCell>
                    <TableCell>Calibre</TableCell>
                    <TableCell align="right">Disp.</TableCell>
                    <TableCell align="right">Peso/U</TableCell>
                    <TableCell align="right" sx={{ minWidth: 110 }}>Cantidad</TableCell>
                    <TableCell align="right">Kilos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stock.map((item) => {
                    const k = stockKey(item);
                    const sel = selected[k];
                    const isSelected = !!sel;
                    const cantidad = sel?.cantidad ?? 0;
                    const kilos = cantidad * item.pesoEmpaqueInterior;
                    const isInvalid = isSelected && (cantidad <= 0 || cantidad > item.cantidadDisponible + 0.0001);
                    return (
                      <TableRow
                        key={k}
                        hover
                        sx={{
                          bgcolor: isSelected ? '#e0f2fe' : undefined,
                          ...(isInvalid && { bgcolor: '#fee2e2' }),
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelected(item)}
                            disabled={isReadOnly}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={origenLabels[item.origen]}
                            color={origenColors[item.origen]}
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>{item.numeroDerc || '-'}</TableCell>
                        <TableCell align="center">{item.numeroGuiaProduccion}</TableCell>
                        <TableCell>{item.descripcionArticulo || '-'}</TableCell>
                        <TableCell>{item.presentacion || '-'}</TableCell>
                        <TableCell>{item.calibre || '-'}</TableCell>
                        <TableCell align="right">{item.cantidadDisponible.toFixed(0)}</TableCell>
                        <TableCell align="right">{item.pesoEmpaqueInterior.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={cantidad || ''}
                            onChange={(e) => updateCantidad(item, e.target.value)}
                            disabled={!isSelected || isReadOnly}
                            inputProps={{
                              min: 0,
                              max: item.cantidadDisponible,
                              step: 1,
                              style: { textAlign: 'right' },
                            }}
                            sx={{ width: 90 }}
                            error={isInvalid}
                          />
                        </TableCell>
                        <TableCell align="right">{kilos.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 4,
              flexWrap: 'wrap',
              fontSize: '0.95rem',
            }}
          >
            <Typography variant="body2">
              <Box component="span" sx={{ color: '#64748b' }}>Items:</Box>{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>{summary.totalItems}</Box>
            </Typography>
            <Typography variant="body2">
              <Box component="span" sx={{ color: '#64748b' }}>Cantidad:</Box>{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>{summary.totalCantidad.toFixed(0)}</Box>
            </Typography>
            <Typography variant="body2">
              <Box component="span" sx={{ color: '#64748b' }}>Total Kilos:</Box>{' '}
              <Box component="span" sx={{ fontWeight: 700, color: '#0f2847' }}>
                {summary.totalKilos.toFixed(2)} kg
              </Box>
            </Typography>
          </Box>
        </FormSectionPanel>

        {/* Acciones */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate('/produccion/guias-salida')}>
            Cancelar
          </Button>
          {!isReadOnly && (
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || summary.totalItems === 0 || summary.invalido}
            >
              {saving ? 'Guardando...' : (editingId != null ? 'Actualizar' : 'Crear Guia')}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default GuiaSalidaFormPage;
